/**
 * This file was auto-generated by Fern from our API Definition.
 */

import * as core from "../../../../core/index.js";
import * as LangfuseAPI from "../../../index.js";
import {
  mergeHeaders,
  mergeOnlyDefinedHeaders,
} from "../../../../core/headers.js";
import * as errors from "../../../../errors/index.js";

export declare namespace ObservationsV2 {
  export interface Options {
    environment: core.Supplier<string>;
    /** Specify a custom URL to connect the client to. */
    baseUrl?: core.Supplier<string>;
    username?: core.Supplier<string | undefined>;
    password?: core.Supplier<string | undefined>;
    /** Override the X-Langfuse-Sdk-Name header */
    xLangfuseSdkName?: core.Supplier<string | undefined>;
    /** Override the X-Langfuse-Sdk-Version header */
    xLangfuseSdkVersion?: core.Supplier<string | undefined>;
    /** Override the X-Langfuse-Public-Key header */
    xLangfusePublicKey?: core.Supplier<string | undefined>;
    /** Additional headers to include in requests. */
    headers?: Record<
      string,
      string | core.Supplier<string | null | undefined> | null | undefined
    >;
  }

  export interface RequestOptions {
    /** The maximum time to wait for a response in seconds. */
    timeoutInSeconds?: number;
    /** The number of times to retry the request. Defaults to 2. */
    maxRetries?: number;
    /** A hook to abort the request. */
    abortSignal?: AbortSignal;
    /** Override the X-Langfuse-Sdk-Name header */
    xLangfuseSdkName?: string | undefined;
    /** Override the X-Langfuse-Sdk-Version header */
    xLangfuseSdkVersion?: string | undefined;
    /** Override the X-Langfuse-Public-Key header */
    xLangfusePublicKey?: string | undefined;
    /** Additional query string parameters to include in the request. */
    queryParams?: Record<string, unknown>;
    /** Additional headers to include in the request. */
    headers?: Record<
      string,
      string | core.Supplier<string | null | undefined> | null | undefined
    >;
  }
}

export class ObservationsV2 {
  protected readonly _options: ObservationsV2.Options;

  constructor(_options: ObservationsV2.Options) {
    this._options = _options;
  }

  /**
   * Get a list of observations with cursor-based pagination and flexible field selection.
   *
   * ## Cursor-based Pagination
   * This endpoint uses cursor-based pagination for efficient traversal of large datasets.
   * The cursor is returned in the response metadata and should be passed in subsequent requests
   * to retrieve the next page of results.
   *
   * ## Field Selection
   * Use the `fields` parameter to control which observation fields are returned:
   * - `core` - Always included: id, traceId, startTime, endTime, projectId, parentObservationId, type
   * - `basic` - name, level, statusMessage, version, environment, bookmarked, public, userId, sessionId
   * - `time` - completionStartTime, createdAt, updatedAt
   * - `io` - input, output
   * - `metadata` - metadata (truncated to 200 chars by default, use `expandMetadata` to get full values)
   * - `model` - providedModelName, internalModelId, modelParameters
   * - `usage` - usageDetails, costDetails, totalCost
   * - `prompt` - promptId, promptName, promptVersion
   * - `metrics` - latency, timeToFirstToken
   *
   * If not specified, `core` and `basic` field groups are returned.
   *
   * ## Filters
   * Multiple filtering options are available via query parameters or the structured `filter` parameter.
   * When using the `filter` parameter, it takes precedence over individual query parameter filters.
   *
   * @param {LangfuseAPI.GetObservationsV2Request} request
   * @param {ObservationsV2.RequestOptions} requestOptions - Request-specific configuration.
   *
   * @throws {@link LangfuseAPI.Error}
   * @throws {@link LangfuseAPI.UnauthorizedError}
   * @throws {@link LangfuseAPI.AccessDeniedError}
   * @throws {@link LangfuseAPI.MethodNotAllowedError}
   * @throws {@link LangfuseAPI.NotFoundError}
   *
   * @example
   *     await client.observationsV2.getMany()
   */
  public getMany(
    request: LangfuseAPI.GetObservationsV2Request = {},
    requestOptions?: ObservationsV2.RequestOptions,
  ): core.HttpResponsePromise<LangfuseAPI.ObservationsV2Response> {
    return core.HttpResponsePromise.fromPromise(
      this.__getMany(request, requestOptions),
    );
  }

  private async __getMany(
    request: LangfuseAPI.GetObservationsV2Request = {},
    requestOptions?: ObservationsV2.RequestOptions,
  ): Promise<core.WithRawResponse<LangfuseAPI.ObservationsV2Response>> {
    const {
      fields,
      expandMetadata,
      limit,
      cursor,
      parseIoAsJson,
      name,
      userId,
      type: type_,
      traceId,
      level,
      parentObservationId,
      environment,
      fromStartTime,
      toStartTime,
      version,
      filter,
    } = request;
    const _queryParams: Record<
      string,
      string | string[] | object | object[] | null
    > = {};
    if (fields != null) {
      _queryParams["fields"] = fields;
    }

    if (expandMetadata != null) {
      _queryParams["expandMetadata"] = expandMetadata;
    }

    if (limit != null) {
      _queryParams["limit"] = limit.toString();
    }

    if (cursor != null) {
      _queryParams["cursor"] = cursor;
    }

    if (parseIoAsJson != null) {
      _queryParams["parseIoAsJson"] = parseIoAsJson.toString();
    }

    if (name != null) {
      _queryParams["name"] = name;
    }

    if (userId != null) {
      _queryParams["userId"] = userId;
    }

    if (type_ != null) {
      _queryParams["type"] = type_;
    }

    if (traceId != null) {
      _queryParams["traceId"] = traceId;
    }

    if (level != null) {
      _queryParams["level"] = level;
    }

    if (parentObservationId != null) {
      _queryParams["parentObservationId"] = parentObservationId;
    }

    if (environment != null) {
      if (Array.isArray(environment)) {
        _queryParams["environment"] = environment.map((item) => item);
      } else {
        _queryParams["environment"] = environment;
      }
    }

    if (fromStartTime != null) {
      _queryParams["fromStartTime"] = fromStartTime;
    }

    if (toStartTime != null) {
      _queryParams["toStartTime"] = toStartTime;
    }

    if (version != null) {
      _queryParams["version"] = version;
    }

    if (filter != null) {
      _queryParams["filter"] = filter;
    }

    let _headers: core.Fetcher.Args["headers"] = mergeHeaders(
      this._options?.headers,
      mergeOnlyDefinedHeaders({
        Authorization: await this._getAuthorizationHeader(),
        "X-Langfuse-Sdk-Name":
          requestOptions?.xLangfuseSdkName ?? this._options?.xLangfuseSdkName,
        "X-Langfuse-Sdk-Version":
          requestOptions?.xLangfuseSdkVersion ??
          this._options?.xLangfuseSdkVersion,
        "X-Langfuse-Public-Key":
          requestOptions?.xLangfusePublicKey ??
          this._options?.xLangfusePublicKey,
      }),
      requestOptions?.headers,
    );
    const _response = await core.fetcher({
      url: core.url.join(
        (await core.Supplier.get(this._options.baseUrl)) ??
          (await core.Supplier.get(this._options.environment)),
        "/api/public/v2/observations",
      ),
      method: "GET",
      headers: _headers,
      queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
      timeoutMs:
        requestOptions?.timeoutInSeconds != null
          ? requestOptions.timeoutInSeconds * 1000
          : 60000,
      maxRetries: requestOptions?.maxRetries,
      abortSignal: requestOptions?.abortSignal,
    });
    if (_response.ok) {
      return {
        data: _response.body as LangfuseAPI.ObservationsV2Response,
        rawResponse: _response.rawResponse,
      };
    }

    if (_response.error.reason === "status-code") {
      switch (_response.error.statusCode) {
        case 400:
          throw new LangfuseAPI.Error(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        case 401:
          throw new LangfuseAPI.UnauthorizedError(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        case 403:
          throw new LangfuseAPI.AccessDeniedError(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        case 405:
          throw new LangfuseAPI.MethodNotAllowedError(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        case 404:
          throw new LangfuseAPI.NotFoundError(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        default:
          throw new errors.LangfuseAPIError({
            statusCode: _response.error.statusCode,
            body: _response.error.body,
            rawResponse: _response.rawResponse,
          });
      }
    }

    switch (_response.error.reason) {
      case "non-json":
        throw new errors.LangfuseAPIError({
          statusCode: _response.error.statusCode,
          body: _response.error.rawBody,
          rawResponse: _response.rawResponse,
        });
      case "timeout":
        throw new errors.LangfuseAPITimeoutError(
          "Timeout exceeded when calling GET /api/public/v2/observations.",
        );
      case "unknown":
        throw new errors.LangfuseAPIError({
          message: _response.error.errorMessage,
          rawResponse: _response.rawResponse,
        });
    }
  }

  protected async _getAuthorizationHeader(): Promise<string | undefined> {
    const username = await core.Supplier.get(this._options.username);
    const password = await core.Supplier.get(this._options.password);
    if (username != null && password != null) {
      return core.BasicAuth.toAuthorizationHeader({
        username: username,
        password: password,
      });
    }

    return undefined;
  }
}
